import { useTheme } from '@mui/material';
import { uniqBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import {
  EmployeeCalendarEvent,
  EmployeeCalendarEventLegend,
} from '~/components/EmployeeCalendar';
import { selectAbsenceColor } from '~/components/EmployeeCalendar/Absences/data';
import { AbsenceType } from '~/data/absences/types';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';

type DynamicEventColors = {
  key: string;
  eventCode: string;
  eventCategory: string;
  legend: EmployeeCalendarEventLegend;
};

type PropsType = {
  events: EmployeeCalendarEvent[];
};

export const useDynamicAbsenceCalendarLegend = (props: PropsType) => {
  const { events } = props;

  const theme = useTheme();

  const { getEnums } = useAllEnums();

  const { dynamicEventColors } = useMemo(() => {
    const shortAbsenceReasonEnums = getEnums(EnumListKey.ABSENCE_SHORT_REASON);
    const longAbsenceReasonEnums = getEnums(EnumListKey.ABSENCE_LONG_REASON);

    const getDynamicEventColor =
      (type: AbsenceType) =>
      (reason: Enum): DynamicEventColors => {
        const { code, name } = reason;

        return {
          key: `${type}_${code}`,
          eventCode: code,
          eventCategory: type,
          legend: {
            key: `${type}_${code}`,
            label: name,
            content: '',
            colors: {
              backgroundColor: selectAbsenceColor(code, name),
              color: theme.palette.common.white,
            },
          },
        };
      };

    const shortColors = shortAbsenceReasonEnums.map(
      getDynamicEventColor(AbsenceType.SHORT),
    );
    const longColors = longAbsenceReasonEnums.map(
      getDynamicEventColor(AbsenceType.LONG),
    );

    return {
      dynamicEventColors: uniqBy(
        shortColors.concat(longColors),
        (dec) => dec.key,
      ),
    };
  }, [getEnums, theme]);

  const getDynamicAbsenceLegend = useCallback(
    (event: EmployeeCalendarEvent): EmployeeCalendarEventLegend | undefined => {
      return dynamicEventColors.find(
        (dec) =>
          dec.eventCategory === event.eventCategory &&
          dec.eventCode === event.eventCode,
      )?.legend;
    },
    [dynamicEventColors],
  );

  const dynamicLegends: EmployeeCalendarEventLegend[] = useMemo(() => {
    return uniqBy(
      events
        .map(getDynamicAbsenceLegend)
        .filter((legend): legend is EmployeeCalendarEventLegend => !!legend),
      ({ key }) => key,
    ).map((legend) => {
      return {
        ...legend,
        visibilityToggle: {
          isEnabled: true,
          isVisible: true,
        },
      };
    });
  }, [events, getDynamicAbsenceLegend]);

  return {
    getDynamicAbsenceLegend,
    dynamicLegends,
  };
};
